import { useCurrentUser, useUsers } from '@centecomm/portal-service-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LaunchDarklyFlags } from 'utils/Hooks/LaunchDarkly/launchDarklyFlags';
import useLaunchDarkly from 'utils/Hooks/LaunchDarkly/useLaunchDarkly';
import { IsSupportedLanguage } from '../Config/SupportedLanguages';

/**
 * This hook is used to determine if the i18n feature is enabled via LaunchDarkly.
 * If the feature is enabled, it does nothing.
 * If the feature is not enabled, it forces the language to English so the app displays as it always did.
 * @returns true if the i18n feature is enabled, false otherwise
 */
function useI18nFeature(): { enabled: boolean; appLanguageFullySet: boolean } {
    const FALLBACK_LANGUAGE = 'en-US';
    const isLocalEnv = APP_ENVIRONMENT === 'local';
    const { getUserLanguage } = useUsers();
    const { currentUserId } = useCurrentUser();
    const { ready: i18nextReady, i18n } = useTranslation();
    const i18nFeatureFlag: boolean | undefined = useLaunchDarkly(LaunchDarklyFlags.I18n);

    const [i18nextDeterminedLanguage, setI18nextDeterminedLanguage] = useState<string | null>(null);
    const [appLanguageFullySet, setAppLanguageFullySet] = useState<boolean>(false);
    const enabled = useMemo(
        () => i18nextReady && i18nFeatureFlag && appLanguageFullySet,
        [i18nextReady, i18nFeatureFlag, appLanguageFullySet],
    );

    useEffect(() => {
        if (i18nextReady) setI18nextDeterminedLanguage(i18n.language || 'en');
    }, [i18nextReady]);

    useEffect(() => {
        const determineAndSetApplicationLanguage = async (): Promise<void> => {
            if (!currentUserId || !i18nextReady || !i18nextDeterminedLanguage || i18nFeatureFlag === undefined) return;

            if (i18nFeatureFlag) {
                if (isLocalEnv) {
                    console.log('I18n feature enabled via LaunchDarkly - setting language to user language');
                }

                const userLanguage = await getUserLanguage(currentUserId);

                if (!userLanguage || !userLanguage.language) {
                    if (isLocalEnv) {
                        console.log('No user language preference found - forcing language to English');
                    }

                    i18n.changeLanguage(FALLBACK_LANGUAGE);
                    setAppLanguageFullySet(true);
                    return;
                }

                if (
                    userLanguage &&
                    userLanguage.language !== i18nextDeterminedLanguage &&
                    IsSupportedLanguage(userLanguage.language)
                ) {
                    if (isLocalEnv) {
                        console.log('User language preference found - setting language to user language');
                    }

                    i18n.changeLanguage(userLanguage.language);
                }

                setAppLanguageFullySet(true);

                return;
            }

            if (isLocalEnv) {
                console.log('I18n feature disabled via LaunchDarkly - forcing language to English');
            }

            i18n.changeLanguage(FALLBACK_LANGUAGE);
            setAppLanguageFullySet(true);
        };

        determineAndSetApplicationLanguage();
    }, [currentUserId, i18n, i18nFeatureFlag, i18nextReady, i18nextDeterminedLanguage]);

    return { enabled: enabled ?? false, appLanguageFullySet };
}

export default useI18nFeature;
